import {
  Dialog,
  DialogConfig,
  DialogRef,
  DIALOG_SCROLL_STRATEGY,
} from '@angular/cdk/dialog';
import {
  OverlayKeyboardDispatcher,
  OverlayOutsideClickDispatcher,
  OverlayRef,
  ScrollStrategy,
} from '@angular/cdk/overlay';
import { ComponentType } from '@angular/cdk/portal';
import { DOCUMENT } from '@angular/common';
import {
  inject,
  Injectable,
  NgZone,
  Provider,
  TemplateRef,
} from '@angular/core';
import {
  MAT_DIALOG_SCROLL_STRATEGY,
  MatDialog,
} from '@angular/material/dialog';

/*
 * SD-3048: the popout's components run in the main window's app, and the CDK's overlay and
 * dialog services are root singletons bound to the main window's document. These are the pieces
 * a dialog opened in a popout needs bound to the popout's document instead. The popout's
 * injector provides DOCUMENT as the popout's, which the classes below inject.
 */

/**
 * The CDK's dispatcher listens for keydown on `'body'` through the root renderer, which resolves
 * it to the main window's body, so Escape pressed in the popout never reached its overlays.
 * Same dispatching as the CDK's, on the popout's body.
 */
@Injectable()
export class PopoutOverlayKeyboardDispatcher extends OverlayKeyboardDispatcher {
  private readonly zone = inject(NgZone);
  private cleanupListener: (() => void) | null = null;

  override add(overlayRef: OverlayRef): void {
    this.remove(overlayRef);
    this._attachedOverlays.push(overlayRef);
    if (this._isAttached) return;

    const body = this._document.body;
    const listener = (event: KeyboardEvent) => this.dispatch(event);
    this.zone.runOutsideAngular(() =>
      body.addEventListener('keydown', listener)
    );
    this.cleanupListener = () => body.removeEventListener('keydown', listener);
    this._isAttached = true;
  }

  protected override detach(): void {
    this.cleanupListener?.();
    this.cleanupListener = null;
    this._isAttached = false;
  }

  private dispatch(event: KeyboardEvent): void {
    const overlays = this._attachedOverlays;
    for (let i = overlays.length - 1; i > -1; i--) {
      const overlayRef = overlays[i];
      if (this.canReceiveEvent(overlayRef, event, overlayRef._keydownEvents)) {
        this.zone.run(() => overlayRef._keydownEvents.next(event));
        break;
      }
    }
  }
}

/**
 * The CDK's BlockScrollStrategy, on the popout's window. The dialogs' default strategy comes from
 * a root token, so it blocked the main window's scrolling, and the CDK class restores the scroll
 * position through the global `window`, which is the main window's.
 */
export class PopoutBlockScrollStrategy implements ScrollStrategy {
  private previous: {
    left: string;
    top: string;
    scrollX: number;
    scrollY: number;
  } | null = null;

  constructor(private readonly doc: Document) {}

  attach(): void {
    // Nothing to attach; the strategy only toggles the popout's root element.
  }

  enable(): void {
    const html = this.doc.documentElement;
    const win = this.doc.defaultView;
    if (
      !win ||
      this.previous ||
      html.classList.contains('cdk-global-scrollblock')
    ) {
      return;
    }
    if (
      html.scrollHeight <= win.innerHeight &&
      html.scrollWidth <= win.innerWidth
    ) {
      return;
    }

    this.previous = {
      left: html.style.left,
      top: html.style.top,
      scrollX: win.scrollX,
      scrollY: win.scrollY,
    };
    html.style.left = `${-win.scrollX}px`;
    html.style.top = `${-win.scrollY}px`;
    html.classList.add('cdk-global-scrollblock');
  }

  disable(): void {
    const previous = this.previous;
    const win = this.doc.defaultView;
    if (!previous) return;
    this.previous = null;

    const html = this.doc.documentElement;
    const body = this.doc.body;
    const htmlBehavior = html.style.scrollBehavior;
    const bodyBehavior = body.style.scrollBehavior;
    html.style.left = previous.left;
    html.style.top = previous.top;
    html.classList.remove('cdk-global-scrollblock');
    // As in the CDK: restore the position instantly, not with any smooth scrolling set by the app.
    html.style.scrollBehavior = body.style.scrollBehavior = 'auto';
    win?.scroll(previous.scrollX, previous.scrollY);
    html.style.scrollBehavior = htmlBehavior;
    body.style.scrollBehavior = bodyBehavior;
  }
}

function popoutScrollStrategyFactory(): () => ScrollStrategy {
  const doc = inject(DOCUMENT);
  return () => new PopoutBlockScrollStrategy(doc);
}

/**
 * The CDK Dialog, for dialogs opened in a popout.
 *
 * - `closeOnNavigation` is off: the overlay would follow the main window's `Location`, so Back in
 *   the main window closed the popout's dialog. The popout has no navigation of its own; when it
 *   unloads, PopoutService closes its dialogs.
 * - Focus is restored in the popout. The CDK container reads the global `document.activeElement`,
 *   the main window's, so it never restored focus in the popout.
 */
@Injectable()
export class PopoutDialog extends Dialog {
  private readonly popoutDocument = inject(DOCUMENT);

  override open<R = unknown, D = unknown, C = unknown>(
    componentOrTemplateRef: ComponentType<C> | TemplateRef<C>,
    config?: DialogConfig<D, DialogRef<R, C>>
  ): DialogRef<R, C> {
    const focusedBefore = this.popoutDocument.activeElement;
    const ref = super.open<R, D, C>(componentOrTemplateRef, {
      ...config,
      closeOnNavigation: false,
    });
    ref.closed.subscribe(() =>
      this.restoreFocus(ref.config.restoreFocus, focusedBefore)
    );
    return ref;
  }

  /** The CDK container's `_restoreFocus`, against the popout's document. */
  private restoreFocus(
    restoreFocus: DialogConfig['restoreFocus'],
    focusedBefore: Element | null
  ): void {
    const doc = this.popoutDocument;
    let target: Element | null = null;
    if (typeof restoreFocus === 'string') {
      target = doc.querySelector(restoreFocus);
    } else if (restoreFocus === true) {
      target = focusedBefore;
    } else if (restoreFocus) {
      target = restoreFocus;
    }
    const focusable = target as HTMLElement | null;

    // Leave focus alone when the user has moved it somewhere else in the meantime.
    const active = doc.activeElement;
    if (
      typeof focusable?.focus === 'function' &&
      focusable.isConnected &&
      (!active || active === doc.body)
    ) {
      focusable.focus();
    }
  }
}

/**
 * The popout's own Dialog and MatDialog look up a parent dialog service with `skipSelf` and, when
 * they find the root one, share its list of open dialogs. Then the main window's `closeAll()`
 * closed the popout's dialogs, and the two levels hid and restored each other's content from
 * assistive technology from one list. An injector with these between the root and the popout's
 * gives them no parent.
 */
export const POPOUT_DIALOG_PARENT_BARRIER: Provider[] = [
  { provide: Dialog, useValue: null },
  { provide: MatDialog, useValue: null },
];

/** Overlay and dialog services for a popout injector whose DOCUMENT is the popout's. */
export const POPOUT_OVERLAY_PROVIDERS: Provider[] = [
  {
    provide: OverlayKeyboardDispatcher,
    useClass: PopoutOverlayKeyboardDispatcher,
  },
  // Listens on `this._document.body`, so the popout's DOCUMENT is enough.
  OverlayOutsideClickDispatcher,
  { provide: DIALOG_SCROLL_STRATEGY, useFactory: popoutScrollStrategyFactory },
  {
    provide: MAT_DIALOG_SCROLL_STRATEGY,
    useFactory: popoutScrollStrategyFactory,
  },
  { provide: Dialog, useClass: PopoutDialog },
  MatDialog,
];
